import React, { memo } from 'react';
import getConfig, { MarketsHeaderOptions, OMFE2ConfigModel } from '@om-fe/core/utils/config';
import { DisciplineLiveStatus } from '@om-fe/models';
import MatchListMixed from '@om-fe/core/components/match-list-mixed/match-list-mixed';
import { MatchListMixedContentTypes } from '@om-fe/core/components/match-list-mixed/match-list-mixed.constants';
import { MixedMatchListTypes } from '@om-fe/widgets/dist/widgets/match-list/match-list.constants';
import { MixedMatchList as MixedMatchListV2 } from '@om-fe/widgets/dist/widgets/match-list/match-list-containers/mixed-match-list';
import { MatchListItemLayout } from '../match-list/utils/match-list.constants';
import { PlatformTypes } from '@om-fe/utils';
import { FavoriteContentTab } from '../favorites/favorites-content-tab';
import MatchOfTheDayMatchListSelector from '../match-list/containers/match-of-the-day-match-list/match-of-the-day-match-list-selector';
import { useI18NContext } from '@om-fe/core/core/i18n/useI18ncontext';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';

interface MatchOfTheDayEventsTabsContentPublicProps {
    readonly disciplineId: string;
    readonly limitNumberOfEvents: number;
    readonly mainMarketsLimit: number;
    readonly instanceId: string;
    readonly marketsHeaderOptions?: MarketsHeaderOptions;
    readonly deviceType?: PlatformTypes;
    readonly dateRange: string;
}

interface MatchOfTheDayEventsTabsContentStoreProps {
    readonly displayChildren?: boolean;
}

type MatchOfTheDayEventsTabsContentProps = MatchOfTheDayEventsTabsContentPublicProps & MatchOfTheDayEventsTabsContentStoreProps;

const MatchOfTheDayTabsContent = memo(function MatchOfTheDayTabsContentComponent
    ({
        disciplineId,
        limitNumberOfEvents,
        marketsHeaderOptions,
        mainMarketsLimit,
        instanceId,
        displayChildren,
        deviceType,
        dateRange,
    }: MatchOfTheDayEventsTabsContentProps
    ) {
        const config: OMFE2ConfigModel = getConfig();
        const marketSelector: boolean = config.matchListConfig?.matchListSelector?.enabled ?? false;
        const showMarketSelectorHeader: boolean = config.matchListConfig?.matchListSelector?.showHeader ?? true;
        const { selectedLanguage }: I18nContextInterface = useI18NContext();

        if (displayChildren) {
            if (config.matchListV2) {
                return (
                    <MixedMatchListV2
                        itemLayout={MatchListItemLayout.Normal}
                        sportId={disciplineId}
                        numberOfMarkets={mainMarketsLimit}
                        contentType={MixedMatchListTypes.Upcoming}
                        liveStatus={DisciplineLiveStatus.BOTH}
                    />
                );
            }

            return (
                <MatchListMixed
                    itemLayout={MatchListItemLayout.Normal}
                    contentType={MatchListMixedContentTypes.MATCH_OF_THE_DAY}
                    instanceId={`MatchOfTheDayEventsTabsContent-${disciplineId}`}
                    liveStatus={DisciplineLiveStatus.BOTH}
                    mainMarketsLimit={mainMarketsLimit}
                    marketsHeaderOptions={marketsHeaderOptions}
                    parentId={disciplineId}
                    hasMarketSelector={marketSelector}
                    dateRange={dateRange}
                />
            )
        }

        if (disciplineId === 'favorites') {
            return (
                <FavoriteContentTab
                    lang= {selectedLanguage ?? 'en'}
                    mainMarketsLimit={mainMarketsLimit}
                />
            );
        }

        return (
            <MatchOfTheDayMatchListSelector
                disciplineId={disciplineId}
                instanceId={instanceId}
                liveStatus={DisciplineLiveStatus.NOT_LIVE}
                limitNumberOfEvents={limitNumberOfEvents}
                marketsHeaderOptions={MarketsHeaderOptions.SHOW_TOURNAMENT}
                mainMarketsLimit={mainMarketsLimit}
                deviceType={deviceType}
                hasMarketSelector={marketSelector}
                showMarketSelectorHeader={showMarketSelectorHeader}
                dateRange={dateRange}
                limit={limitNumberOfEvents}
            />
        );
    }
);

export default MatchOfTheDayTabsContent;
